import { z } from '@customerio/hydra-validation';
import type { BroadcastTrigger as BroadcastTriggerWire, CampaignsSendTimingResponse } from '@customerio/hydra-ui-api';
import { readSchema } from '../shared/define-entity';

/**
 * Broadcast trigger — one run of an API-triggered broadcast: a call to its
 * trigger endpoint (from the API or the Triggering Details tab), with the
 * audience and data it was sent with.
 *
 * Triggers are read per broadcast and never written back, so this module
 * hand-rolls its keys under the `['workspace', id]` subtree, the way
 * `recent-trigger` does, rather than calling `defineEntity`.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

/**
 * A broadcast trigger as this layer holds it.
 *
 * `id` is the composite `"<campaign_id>-<n>"` the API keys a trigger by, and
 * the one the trigger and its deliveries are read with. `campaign_id` is a
 * string id, like every campaign id here; the wire sends a number.
 *
 * `data` and `recipients_filter` stay the JSON strings the wire sends.
 * `expires_after` is in seconds, 0 for no expiry, as are the `*_at` times
 * (Unix seconds, 0 for not yet).
 */
export type BroadcastTrigger = Omit<BroadcastTriggerWire, 'campaign_id'> & { campaign_id: string };

/** A broadcast's send counts over time, per trigger, for its send-timing chart. */
export type BroadcastSendTiming = CampaignsSendTimingResponse['send_timing'];

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

const BroadcastTriggerFromApi = readSchema<BroadcastTrigger>(
  z.looseObject({
    campaign_id: z.number().transform(String),
  }),
);

/** @public */
export function parseBroadcastTrigger(raw: BroadcastTriggerWire): BroadcastTrigger {
  return BroadcastTriggerFromApi.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Keys                                                                       */
/* ------------------------------------------------------------------------- */

export const broadcastTriggerKeys = {
  all: (workspaceId: number, campaignId: string) =>
    ['workspace', workspaceId, 'broadcast-trigger', campaignId] as const,
  /** One page of a broadcast's triggers. */
  list: (workspaceId: number, campaignId: string, page: number) =>
    [...broadcastTriggerKeys.all(workspaceId, campaignId), 'list', page] as const,
  detail: (workspaceId: number, campaignId: string, triggerId: string) =>
    [...broadcastTriggerKeys.all(workspaceId, campaignId), 'detail', triggerId] as const,
  /** The broadcast's latest trigger, which `GET /campaigns/:id` sideloads. */
  mostRecent: (workspaceId: number, campaignId: string) =>
    [...broadcastTriggerKeys.all(workspaceId, campaignId), 'most-recent'] as const,
  sendTiming: (workspaceId: number, campaignId: string, window: SendTimingWindow) =>
    [...broadcastTriggerKeys.all(workspaceId, campaignId), 'send-timing', window] as const,
};

/**
 * The window a send-timing read covers. With neither bound the server picks
 * the latest trigger's own window. `triggerNumber` narrows it to one trigger.
 */
export type SendTimingWindow = {
  /** Unix seconds. */
  start?: number;
  /** Unix seconds. */
  end?: number;
  triggerNumber?: number;
};

/* ------------------------------------------------------------------------- */
/* Selectors                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * A trigger's own number, the part of its composite id after the campaign's.
 * Send timing and the deliveries export take this rather than the composite
 * id; the trigger and its deliveries take the composite one.
 */
export function triggerNumber(trigger: Pick<BroadcastTrigger, 'id'>): number {
  return Number(trigger.id.slice(trigger.id.lastIndexOf('-') + 1));
}

/** Whether the trigger discards what it hasn't sent after a while. */
export function hasSendExpiry(trigger: Pick<BroadcastTrigger, 'expires_after'>): boolean {
  return trigger.expires_after > 0;
}
